import { useCallback, useEffect, useState } from "react";
import useApiRequest from "../../lib/hooks/useRequest";
import { OrganizationListResponseDto } from "../../lib/interfaces/organizations";

/**
 * Die Betriebe, die diese Person oben sehen will (KAN-298).
 *
 * Eigener Weg statt eines Feldes an der Liste: Die Liste ist seitenweise, die Anheftungen
 * sind es nicht - sie sollen oben stehen, egal auf welcher Seite der Betrieb sonst laege.
 *
 * Die Oberflaeche aendert ihren Stand sofort und holt danach frisch nach. Ein Pinnadel-Klick
 * soll sich nicht anfuehlen wie ein Formular, das erst abschickt.
 */
export const useAngeheftete = () => {
  const { apiRequest } = useApiRequest();
  const [angeheftete, setAngeheftete] = useState<OrganizationListResponseDto[]>([]);
  const [grenzeErreicht, setGrenzeErreicht] = useState(false);

  const laden = useCallback(async () => {
    const antwort = await apiRequest<OrganizationListResponseDto[]>("organizations/pinned", "GET");
    // Nur etwas, das wirklich wie eine Liste aussieht, wird eine: Scheitert das Lesen der
    // Antwort, reicht useRequest den Platzhalter "success" durch.
    if (Array.isArray(antwort.data)) setAngeheftete(antwort.data);
  }, [apiRequest]);

  useEffect(() => {
    laden();
  }, [laden]);

  const umschalten = useCallback(
    async (orga: OrganizationListResponseDto, anheften: boolean): Promise<void> => {
      setGrenzeErreicht(false);

      // Erst zeigen, dann fragen: Der Stand wird gleich ohnehin frisch geholt, und bei einer
      // Absage steht die alte Liste wieder da.
      setAngeheftete(bisher => (anheften ? [...bisher, orga] : bisher.filter(eintrag => eintrag._id !== orga._id)));

      const antwort = await apiRequest(`organizations/${orga._id}/pin`, anheften ? "POST" : "DELETE", {
        withoutErrorModal: true,
      });

      // 400 heisst hier genau eine Sache: mehr geht nicht.
      if (antwort.status === 400) setGrenzeErreicht(true);
      await laden();
    },
    [apiRequest, laden]
  );

  const istAngeheftet = useCallback(
    (id: string): boolean => angeheftete.some(eintrag => eintrag._id === id),
    [angeheftete]
  );

  return { angeheftete, istAngeheftet, umschalten, grenzeErreicht, laden };
};
